import * as React from 'react';
import { cloneDeep, flattenDeep } from 'lodash';
import { Empty } from 'antd';
import { DiffNodeComp } from './diff-node-comp';
import { DiffNodeConfig, DiffNodeRenderType, DiffFieldConfig } from './node-config';
import { CONFLICT_RESOLVE_TYPE, PAGE_TYPE } from '@config/index';
import { isEqual, getAgentId } from '@lib/index';
import { localizedString } from '@locale/index';
import { DiffStates, agentToServer } from '@interface/index';
import { camelToSnake } from '@lib/parse-params';

function getFieldData(data: any, fieldConfig: DiffFieldConfig) {
  return data[fieldConfig.key];
}

function nodeEqual(obj1: any, obj2: any, node?: DiffNodeConfig) {

  if (obj1 === obj2) {
    return true;
  }

  if (!obj1 || !obj2) {
    return false;
  }

  if (!node) {
    return isEqual(obj1, obj2);
  }

  let ret = true;

  for (const field of node.fields) {
    const v1 = getFieldData(obj1, field);
    const v2 = getFieldData(obj2, field);

    if (!v1 && !v2) {
      continue;
    }

    if (!v1 || !v2) {
      ret = false;
      break;
    }

    if (field.isArray) {

      if (v1.length !== v2.length) {
        ret = false;
        break;
      }

      for (let i = 0; i < v1.length; i += 1) {
        const item1 = v1[i];
        const item2 = v2[i];

        const eq = field.node
          ? nodeEqual(item1, item2, field.node)
          : isEqual(item1, item2);
        if (!eq) {
          ret = false;
          break;
        }
      }
    } else {

      const eq = field.node
        ? nodeEqual(v1, v2, field.node)
        : isEqual(v1, v2);
      if (!eq) {
        ret = false;
        break;
      }
    }

    if (!ret) {
      break;
    }
  }

  return ret;
}

function getName(data: any, node?: DiffNodeConfig) {
  if (!node ||
    node.page === PAGE_TYPE.PHRASE ||
    node.page === PAGE_TYPE.PROMPT
  ) {
    return undefined;
  }

  let result = cloneDeep(data);

  if (node.nameKey) {
    const paths = node.nameKey.split('.');

    for (const path of paths) {
      result = result[path];
    }
  } else {
    result = data['name'];
  }

  if (node.page === PAGE_TYPE.INTENT ||
    node.page === PAGE_TYPE.COMPOSITE ||
    node.page === PAGE_TYPE.ENTITY) {
    if (data.qualification && data.qualification.length > 0) {
      result = `${data.qualification}.${result}`;
    }

    if (data.source && data.source.length > 0) {
      result = `${data.source}.${result}`;
    }
  }

  return result;
}

interface ResolvedNode {
  action: 'delete' | 'patch';
  page: string;
  body: {
    key: {
      meta_id?: string;
      agent_id?: string;
      owner_id?: string;
      frame_id?: string;
    },
    data: any;
  };
}

interface ReviewCompProps {
  resolveAgentId?: string;
  basePath: string[];
  rootNode?: DiffNodeConfig;
  data: any;
  conflicting?: boolean;
  onResolve?: (data: any) => void;
}

interface ReviewCompState {
  resolvedList: ResolvedNode[];
}

export class ReviewComp extends React.Component<
  ReviewCompProps,
  ReviewCompState
> {

  constructor(props: ReviewCompProps) {
    super(props);

    this.state = {
      resolvedList: []
    };
  }

  shouldComponentUpdate(nextProps: Readonly<ReviewCompProps>, nextState: Readonly<ReviewCompState>, nextContext: any): boolean {
    if (!isEqual(nextProps.data, this.props.data)) {
      return true;
    }

    return !isEqual(nextState, this.state);

  }

  renderNode = ({
    data,
    masterData,
    node,
    basePath = [],
    onResolve
  }: {
    data: any;
    masterData?: any;
    node: DiffNodeConfig;
    basePath?: string[];
    onResolve?: (data: any) => void;
  }): React.ReactNode[] => {
    const resolvedData: any = cloneDeep(data);

    return flattenDeep(node.fields
      .filter((field: DiffFieldConfig) => {

        if (field.hasMasterSelf) {

          let tmpMaster = (data.masterSelf && getFieldData(data.masterSelf, field))
            || (masterData && getFieldData(masterData, field));
          let tmpLocal = data && getFieldData(data, field);

          if (this.props.conflicting
            && (field.key === node.nameKey
              || field.key === 'name')) {
            return tmpMaster !== undefined &&
              tmpMaster !== null &&
              tmpMaster !== '' &&
              tmpMaster !== tmpLocal;
          }

          if (this.props.conflicting
            && field.key === 'qualification') {
            return tmpMaster !== undefined &&
              tmpMaster !== null &&
              tmpMaster !== '' &&
              tmpMaster !== tmpLocal;
          }

          if (field.isArray) {
            tmpMaster = tmpMaster || [];
            tmpLocal = tmpLocal || [];
          } else {
            tmpMaster = tmpMaster || {};
            tmpLocal = tmpLocal || {};
          }

          if (field.isArray) {
            if (tmpMaster.length !== tmpLocal.length) {
              return true;
            }
            let diff = false;
            for (const [i, masterItem] of tmpMaster.entries()) {
              const localItem = tmpLocal[i];

              const same = field.node
                ? nodeEqual(masterItem, localItem, field.node)
                : isEqual(masterItem, localItem);
              if (!same) {
                diff = true;
                break;
              }
            }

            return diff;
          }

          const ret = !!field.node
            ? !nodeEqual(tmpMaster, tmpLocal, field.node)
            : !isEqual(tmpMaster, tmpLocal);

          return ret;
        }

        const tmpData = getFieldData(data, field);

        if (field.isArray) {
          for (const item of (tmpData || [])) {
            if (item.state === DiffStates.PRIVATE_CONFLICT
              || item.state === DiffStates.ADD
              || item.state === DiffStates.DEL) {
              return true;
            }
          }
        } else {

          if (tmpData && (tmpData.state === DiffStates.PRIVATE_CONFLICT
            || tmpData.state === DiffStates.ADD
            || tmpData.state === DiffStates.DEL)) {
            return true;
          }

        }
        return false;
      })
      .map((field: DiffFieldConfig) => {

        const tmpLocal = data && getFieldData(data, field);
        const tmpPath = cloneDeep(basePath || []);
        if (field.localeNameKey) {
          tmpPath.push(localizedString(field.localeNameKey));
        } else {
          tmpPath.push(field.name || field.key);
        }

        if (field.hasMasterSelf) {

          const tmpMaster = (data.masterSelf && getFieldData(data.masterSelf, field))
            || (masterData && getFieldData(masterData, field));

          return this.getDiffComp({
            node: field.node,
            isArray: field.isArray,
            masterData: tmpMaster,
            localData: tmpLocal,
            renderType: field.renderType,
            path: [
              ...(basePath || []),
              (field.localeNameKey
                ? localizedString(field.localeNameKey)
                : field.name || '')
            ],
            onResolve: (selectedData: any) => {
              resolvedData[field.key] = selectedData;
              if (onResolve) {
                onResolve(resolvedData);
              }
            }
          });
        }

        if (!field.isArray && (tmpLocal.state && tmpLocal.state !== DiffStates.NORMAL)) {
          const tmpMaster = tmpLocal.masterSelf;
          delete tmpLocal.masterSelf;

          return this.getDiffComp({
            node: field.node,
            isArray: field.isArray,
            masterData: tmpMaster,
            localData: tmpLocal,
            renderType: field.renderType,
            path: [
              ...(basePath || []),
              (field.localeNameKey
                ? localizedString(field.localeNameKey)
                : field.name || '')
            ],
            onResolve: (selectedData: any) => {
              resolvedData[field.key] = selectedData;
              if (onResolve) {
                onResolve(resolvedData);
              }
            }
          });
        }

        const renderArr = [];
        const items = tmpLocal.filter((item: any) =>
          item.state !== DiffStates.MASTER_CONFLICT);

        for (const [index, item] of items.entries()) {
          const nextPath = cloneDeep(tmpPath);
          nextPath.push(
            field.node &&
            getName(item, field.node) ||
            `[${index}]`
          );

          const resolveArr = (selectedData: any) => {
            if (field.node && field.node.page) {

              const serverData = selectedData
                && field.node.funcToServer
                && field.node.funcToServer(selectedData);

              // const changePart = {
              //   [camelToSnake(field.key)]: serverData[camelToSnake(field.key)]
              // }

              const resolvedNode: ResolvedNode = {
                action: selectedData ? 'patch' : 'delete',
                page: field.node.page || '',
                body: {
                  key: {
                    agent_id: this.props.resolveAgentId || getAgentId() || '',
                    meta_id: item[field.node.idKey || 'id'],
                    owner_id: item.ownerId,
                    frame_id: item.frameId
                  },
                  data: serverData
                }
              };
              setTimeout(() => {
                this.updateResolvedList(resolvedNode);
              });
            } else {
              resolvedData[field.key] = selectedData;
              if (onResolve) {
                onResolve(resolvedData);
              }
            }
          };

          if (item.state === DiffStates.PRIVATE_CONFLICT) {


            let masterItem = tmpLocal.find((it: any) =>
              it.id === item.id && it.state === DiffStates.MASTER_CONFLICT);

            if (!masterItem) {
              masterItem = item.masterSelf;
            }

            if (field.node && !field.isLeaf) {
              const renderItem = this.renderNode({
                node: field.node,
                data: item,
                masterData: masterItem,
                basePath: nextPath,
                onResolve: resolveArr
              });
              renderArr.push(renderItem);
            } else {
              renderArr.push(this.getDiffComp({
                node: field.node,
                masterData: masterItem,
                localData: item,
                path: nextPath,
                renderType: field.renderType,
                onResolve: resolveArr
              }));
            }
          } else if (item.state === DiffStates.ADD) {
            renderArr.push(this.getDiffComp({
              node: field.node,
              localData: item,
              path: nextPath,
              renderType: field.renderType,
              onResolve: resolveArr
            }));
          } else if (item.state === DiffStates.DEL) {
            renderArr.push(this.getDiffComp({
              node: field.node,
              masterData: item,
              path: nextPath,
              renderType: field.renderType,
              onResolve: resolveArr
            }));
          }
        }

        return renderArr;
      }));
  };

  render() {
    const {
      rootNode,
      basePath,
      data,
      conflicting
    } = this.props;

    const comp = rootNode && this.renderNode({
      data,
      basePath,
      node: rootNode,
      onResolve: conflicting ? (resolvedData: any) => {

        //   const serverData = resolvedData
        //   && field.node.funcToServer
        //   && field.node.funcToServer(resolvedData);

        // const changePart = serverData[camelToSnake(field.key)]

        const resolvedNode: ResolvedNode = {
          action: 'patch',
          page: rootNode.page || '',
          body: {
            key: {
              agent_id: this.props.resolveAgentId || getAgentId() || '',
              meta_id: data.id
            },
            data: rootNode &&
              rootNode.funcToServer &&
              rootNode.funcToServer(resolvedData)
          }
        };
        this.updateResolvedList(resolvedNode);
      } : undefined
    });

    return (
      <div className="diff-comp">
        {
          comp && comp.length > 0 && comp || <Empty />
        }
      </div>
    );
  }

  getDiffComp = ({
    path = [],
    isArray,
    masterData,
    localData,
    node,
    renderType,
    onResolve
  }: {
    path?: string[];
    isArray?: boolean;
    masterData?: any;
    localData?: any;
    node?: DiffNodeConfig;
    renderType?: DiffNodeRenderType;
    onResolve?: (selectedData: any) => void;
  }) => {

    return (
      <DiffNodeComp
        key={path.join('/')}
        path={path}
        masterData={masterData}
        localData={localData}
        node={node}
        renderType={renderType}
        isArray={isArray}
        conflicting={this.props.conflicting}
        onResolve={(choice: CONFLICT_RESOLVE_TYPE) => {
          if (onResolve) {
            const selectedData = cloneDeep(
              choice === CONFLICT_RESOLVE_TYPE.MASTER
                ? masterData
                : localData
            );

            if (selectedData) {
              delete selectedData.state;
            }

            onResolve(selectedData);
          }
        }}
      />
    );
  }

  updateResolvedList = (node: ResolvedNode) => {

    const tmpData = cloneDeep(node.body.data);

    if (tmpData) {
      for (const key of Object.keys(node.body.data)) {
        if (tmpData[key] === undefined || tmpData[key] === null) {
          delete tmpData[key];
        }
      }

      node.body.data = tmpData;
    }

    const { resolvedList } = this.state;
    const tmpList = cloneDeep(resolvedList);

    let found = false;
    for (const [i, item] of resolvedList.entries()) {
      if (
        item.page === node.page &&
        isEqual(item.body.key, node.body.key)
      ) {
        tmpList.splice(i, 1, node);
        found = true;
        break;
      }
    }

    if (!found) {
      tmpList.push(node);
    }

    this.setState({
      resolvedList: tmpList
    }, () => {
      if (this.props.onResolve) {
        this.props.onResolve(tmpList);
      }
    });
  }
}
